/**
 * 英語表記住所（EnglishAddress）の国別フィールド定義（v3.x / 2026-07）。
 *
 * データは EnglishAddress 共通スキーマ（country / postalCode / stateRegion / city / line1 / line2）で
 * 保存し、UI（顧客編集ダイアログ・公開フォーム）は本ファイルの国別設定を参照して
 * 「表示するフィールド・順序・ラベル（英/日）・プレースホルダ」を出し分ける。
 *
 * 「主要国ごとに専用フィールド」を、共通スキーマ上に国別の見せ方として実装したもの。
 */
import type { EnglishAddress } from '../types/mail';

/** EnglishAddress の country 以外のキー。 */
export type EnglishAddressFieldKey = Exclude<keyof EnglishAddress, 'country'>;

export interface CountryAddressField {
  key: EnglishAddressFieldKey;
  /** 英語ラベル（公開フォーム / 英語表記用）。 */
  label: string;
  /** 日本語ラベル（管理画面用）。 */
  labelJa: string;
  placeholder?: string;
}

export interface CountryAddressConfig {
  /** 国コード（EnglishAddress.country に保存する値）。 */
  code: string;
  /** 表示名（英語）。 */
  name: string;
  /** 表示名（日本語）。 */
  nameJa: string;
  /** 表示するフィールドと順序・ラベル。 */
  fields: CountryAddressField[];
}

// フィールド定義のショートハンド
const F = (
  key: EnglishAddressFieldKey,
  label: string,
  labelJa: string,
  placeholder?: string,
): CountryAddressField => ({ key, label, labelJa, placeholder });

/**
 * 主要国の住所フィールド設定。
 * 末尾の 'OTHER' は汎用フォールバック（不明な国コードもこれで表示）。
 */
export const COUNTRY_ADDRESS_CONFIGS: CountryAddressConfig[] = [
  {
    code: 'JP',
    name: 'Japan',
    nameJa: '日本',
    fields: [
      F('postalCode', 'Postal code', '郵便番号', '123-4567'),
      F('stateRegion', 'Prefecture', '都道府県', 'Tokyo'),
      F('city', 'City / Ward', '市区町村', 'Minato-ku'),
      F('line1', 'Town / Block / Number', '町名・番地', '5-13-1 Minami-Aoyama'),
      F('line2', 'Building / Room', '建物名・部屋番号', 'Prefas Minami-Aoyama 5F'),
    ],
  },
  {
    code: 'US',
    name: 'United States',
    nameJa: 'アメリカ',
    fields: [
      F('line1', 'Street address', '番地・通り', '1600 Amphitheatre Pkwy'),
      F('line2', 'Apt / Suite', 'アパート・部屋番号', 'Suite 100'),
      F('city', 'City', '市', 'Mountain View'),
      F('stateRegion', 'State', '州', 'CA'),
      F('postalCode', 'ZIP code', 'ZIPコード', '94043'),
    ],
  },
  {
    code: 'DE',
    name: 'Germany',
    nameJa: 'ドイツ',
    fields: [
      F('line1', 'Street and number', '通り・番地', 'Musterstraße 12'),
      F('line2', 'Address line 2', '住所2行目'),
      F('postalCode', 'Postal code (PLZ)', '郵便番号', '10115'),
      F('city', 'City', '市', 'Berlin'),
      F('stateRegion', 'State (Bundesland)', '州', 'Berlin'),
    ],
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    nameJa: 'イギリス',
    fields: [
      F('line1', 'Address line 1', '住所1行目', '10 Downing Street'),
      F('line2', 'Address line 2', '住所2行目'),
      F('city', 'Town / City', '市', 'London'),
      F('stateRegion', 'County', '州・郡'),
      F('postalCode', 'Postcode', '郵便番号', 'SW1A 2AA'),
    ],
  },
  {
    code: 'FR',
    name: 'France',
    nameJa: 'フランス',
    fields: [
      F('line1', 'Street and number', '通り・番地', '55 Rue du Faubourg'),
      F('line2', 'Address line 2', '住所2行目'),
      F('postalCode', 'Postal code', '郵便番号', '75008'),
      F('city', 'City', '市', 'Paris'),
      F('stateRegion', 'Region', '地域圏'),
    ],
  },
  {
    code: 'CN',
    name: 'China',
    nameJa: '中国',
    fields: [
      F('stateRegion', 'Province', '省', 'Guangdong'),
      F('city', 'City', '市', 'Shenzhen'),
      F('line1', 'Street address', '番地・通り'),
      F('line2', 'Address line 2', '住所2行目'),
      F('postalCode', 'Postal code', '郵便番号', '518000'),
    ],
  },
  {
    code: 'KR',
    name: 'South Korea',
    nameJa: '韓国',
    fields: [
      F('stateRegion', 'Province / City', '道・広域市', 'Seoul'),
      F('city', 'City / District', '市・区', 'Gangnam-gu'),
      F('line1', 'Street address', '番地・通り'),
      F('line2', 'Address line 2', '住所2行目'),
      F('postalCode', 'Postal code', '郵便番号', '06236'),
    ],
  },
  {
    code: 'OTHER',
    name: 'Other',
    nameJa: 'その他',
    fields: [
      F('line1', 'Address line 1', '住所1行目'),
      F('line2', 'Address line 2', '住所2行目'),
      F('city', 'City', '市'),
      F('stateRegion', 'State / Region', '州・地域'),
      F('postalCode', 'Postal / ZIP code', '郵便番号'),
    ],
  },
];

// ─────────────────────────────────────────────
// フォーム質問 english_profile の回答値エンコード（パイプ区切り）
// billing_address 等と同じく FormSubmission.values に文字列で格納するため。
// ─────────────────────────────────────────────

/** english_profile 回答値のフィールド順（パイプ区切りのインデックス）。 */
export const ENGLISH_PROFILE_FIELD_ORDER = [
  'nameEn',
  'phoneIntl',
  'country',
  'postalCode',
  'stateRegion',
  'city',
  'line1',
  'line2',
] as const;

export type EnglishProfileKey = (typeof ENGLISH_PROFILE_FIELD_ORDER)[number];

export type EnglishProfileValue = Record<EnglishProfileKey, string>;

export function emptyEnglishProfile(): EnglishProfileValue {
  return {
    nameEn: '',
    phoneIntl: '',
    country: '',
    postalCode: '',
    stateRegion: '',
    city: '',
    line1: '',
    line2: '',
  };
}

/**
 * EnglishProfileValue → パイプ区切り文字列（値内の '|' は空白に置換）。
 *
 * ⚠️ ここで trim してはいけない (2026-08-25 修正)。
 * 公開フォームの入力欄は「decode → 編集 → encode」を **1 打鍵ごとに往復する**
 * 制御コンポーネントのため、trim すると入力途中の末尾スペースがその場で消え、
 * 単語区切り (例: "Taro Yamada" の空白) を打てなくなる。
 * 前後空白の除去は保存側 (onFormSubmissionCreated / buildContactPatch) が行う。
 */
export function encodeEnglishProfile(v: EnglishProfileValue): string {
  return ENGLISH_PROFILE_FIELD_ORDER.map((k) =>
    (v[k] ?? '').replace(/\|/g, ' '),
  ).join('|');
}

/**
 * パイプ区切り文字列 → EnglishProfileValue（欠損は空文字）。
 * encode と対でこちらも trim しない（入力途中の空白を保つため。上記コメント参照）。
 */
export function decodeEnglishProfile(
  raw: string | null | undefined,
): EnglishProfileValue {
  const parts = (raw ?? '').split('|');
  const out = emptyEnglishProfile();
  ENGLISH_PROFILE_FIELD_ORDER.forEach((k, i) => {
    out[k] = parts[i] ?? '';
  });
  return out;
}

/** 保存直前の正規化: 各項目の前後空白を除去する（内部の空白は保持）。 */
export function trimEnglishProfile(v: EnglishProfileValue): EnglishProfileValue {
  const out = emptyEnglishProfile();
  ENGLISH_PROFILE_FIELD_ORDER.forEach((k) => {
    out[k] = (v[k] ?? '').trim();
  });
  return out;
}

/** country コードに対応する設定を返す（未知/未設定は OTHER）。 */
export function getCountryAddressConfig(
  country: string | null | undefined,
): CountryAddressConfig {
  const code = (country ?? '').trim().toUpperCase();
  return (
    COUNTRY_ADDRESS_CONFIGS.find((c) => c.code === code) ??
    COUNTRY_ADDRESS_CONFIGS[COUNTRY_ADDRESS_CONFIGS.length - 1]
  );
}

/**
 * EnglishAddress を 1 行の英語表記文字列に整形する（メール本文等で使う）。
 * 国別設定の順序でフィールドを並べ、空を除いてカンマ区切り + 末尾に国名。
 */
export function formatEnglishAddress(
  addr: EnglishAddress | null | undefined,
): string {
  if (!addr) return '';
  const config = getCountryAddressConfig(addr.country);
  const parts = config.fields
    .map((f) => (addr[f.key] ?? '').toString().trim())
    .filter(Boolean);
  const line = parts.join(', ');
  const countryName = addr.country ? config.name : '';
  return [line, countryName].filter(Boolean).join(', ');
}
